import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { localeFor } from "./calendarUtils";
// The refusal-code table is shared with the calendar's drag-and-drop, which reaches the
// same door. One table, so a new server refusal cannot be handled in one surface and
// swallowed in the other.
import { rescheduleErrKey } from "./rescheduleErrors";

// The practitioner's LOCAL calendar day of an instant ("2026-09-28"), so a 23:30 start
// is listed under the day the practitioner reads on their own clock — never under the
// UTC date, which is the next day for anyone west of Greenwich.
const localDayKey = (iso) => {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// [{start}] (sorted by the server) → [{day, starts: [{start}]}], days in order.
const groupByDay = (list) => {
  const map = new Map();
  for (const s of list) {
    const key = localDayKey(s.start);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  return [...map.keys()].sort().map((day) => ({
    day,
    starts: map.get(day).sort((a, b) => new Date(a.start) - new Date(b.start)),
  }));
};

/**
 * Admin reschedule dialog — opened from BookingsAdmin.jsx's per-row action and from
 * EventDetailDialog.jsx. `booking` needs at least {id, service}.
 *
 * The times listed are the server's own answer for THIS appointment
 * (GET /admin/bookings/{id}/offered-starts): the starts its service offers now, computed
 * with the appointment's own booked length and its own time left out — so it can move by
 * half an hour over itself. A reschedule keeps the service, the length and the buffer it
 * was sold with; only the start moves, so there is nothing else to choose.
 *
 * A time taken between this read and the click is refused (TIME_UNAVAILABLE) and the
 * list is read again, so the practitioner picks from what is really free.
 */
export default function RescheduleDialog({ booking, onClose, onChanged }) {
  const t = useT("admin");
  const { lang } = useLanguage();
  const [starts, setStarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const { data } = await api.get(`/admin/bookings/${booking.id}/offered-starts`);
      if (!Array.isArray(data)) throw new Error("offered-starts: not a list");
      setStarts(data);
    } catch {
      // A failed read is not "no times": saying there is nothing free when nobody could
      // look would be a false statement about the practitioner's calendar.
      setStarts([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [booking.id]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => groupByDay(starts), [starts]);

  const submit = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/admin/bookings/${booking.id}/reschedule`, { new_start: selected });
      toast.success(t("reschedule.success"));
      onChanged?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const key = rescheduleErrKey(detail);
      setError(key ? t(key) : extractApiError(e, t("avail.error")));
      if (detail === "TIME_UNAVAILABLE") {
        setSelected(null);
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  const timeFmt = (iso) => new Date(iso).toLocaleTimeString(localeFor(lang), { hour: "2-digit", minute: "2-digit" });
  const dayFmt = (day) => new Date(`${day}T00:00:00`).toLocaleDateString(localeFor(lang), { weekday: "long", day: "numeric", month: "long" });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>{t("reschedule.title")}</DialogTitle>
          <DialogDescription>{t("reschedule.subtitle", { service: booking.service })}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-eh-muted" size={20} /></div>
        ) : (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
            {loadFailed && (
              <div className="text-sm text-red-600" role="alert" data-testid="reschedule-load-error">
                {t("reschedule.loadError")}{" "}
                <button type="button" onClick={load} className="underline" data-testid="reschedule-retry">{t("reschedule.retry")}</button>
              </div>
            )}
            {!loadFailed && days.length === 0 && (
              <p className="text-sm text-eh-muted" data-testid="reschedule-no-slots">{t("reschedule.noSlots")}</p>
            )}
            {days.length > 0 && (
              <RadioGroup value={selected || ""} onValueChange={setSelected} className="space-y-4">
                {days.map(({ day, starts: dayStarts }) => (
                  <div key={day} data-testid={`reschedule-day-${day}`}>
                    <div className="text-xs font-medium text-eh-ink capitalize mb-1">{dayFmt(day)}</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                      {dayStarts.map((s) => (
                        <label
                          key={s.start}
                          className="flex items-center gap-2 text-sm border border-eh-border-soft rounded-lg px-3 py-2 cursor-pointer hover:border-eh-ink transition"
                          data-testid={`reschedule-start-${s.start}`}
                        >
                          <RadioGroupItem value={s.start} data-testid={`reschedule-start-radio-${s.start}`} />
                          {timeFmt(s.start)}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            )}

            {error && <p className="text-sm text-red-600" role="alert" data-testid="reschedule-error">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="reschedule-close">
            {t("reschedule.close")}
          </Button>
          <Button onClick={submit} disabled={!selected || busy} data-testid="reschedule-submit">
            {busy ? <Loader2 className="animate-spin" size={14} /> : null} {t("reschedule.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
